import React from 'react'
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom'
import { Box, Card, Typography, Divider, Link, Skeleton } from '@mui/material'
import { useQuery } from 'react-query'
import AddIcon from '@mui/icons-material/Add'

// Local Import
import AdxInfoIcon from 'components/custom/AdxInfoIcon'
import LeavePolicyModal from './LeavePolicyModal'

// API service
import { getLeavePolicies } from 'services/api/modules/leave/leavePolicy'
import AdxButton from 'components/custom/AdxButton'

const LeavePolicy = ({ sx, ...rest }) => {
  let location = useLocation()
  const navigate = useNavigate()
  //CALLING API
  const { data, isLoading } = useQuery('leave-policy', async () => {
    const { data: apiData } = await getLeavePolicies()
    return apiData.data
  })

  let skeletonArray = new Array(5).fill('i')

  return (
    <>
      {/* With Entitlement */}
      <Card
        variant="outlined"
        sx={{
          mb: 3,
          boxSizing: 'border-box',
          borderRadius: '4.5px',
        }}
      >
        <Box display={'flex'} flexDirection={'column'}>
          <Box
            sx={{
              p: 2,
              display: 'flex',
              alignItems: 'center',
              boxSizing: 'border-box',
            }}
          >
            <Typography sx={{ fontWeight: 'bold' }} variant="subtitle1">
              Policy with entitlement
            </Typography>
            <AdxInfoIcon
              sx={{ ml: 2 }}
              toolTipTitle={
                'Policies that are automatically allocated to all employees in the organization. All employees will be able to view their allocated amount.'
              }
            />
          </Box>
          <Divider variant="full" />
          {isLoading
            ? skeletonArray.map((arr, index) => (
                <React.Fragment key={index}>
                  <Box
                    sx={{
                      display: { xs: 'column', sm: 'flex' },
                      justifyContent: 'space-between',
                    }}
                  >
                    <Box p={{ xs: 1, sm: 2 }} width={{ xs: 1, sm: 1 / 3 }}>
                      <Skeleton width={'100%'} height={35} />
                    </Box>
                    <Box p={{ xs: 1, sm: 2 }} width={{ xs: 1, sm: 1 / 4 }}>
                      <Skeleton width={'100%'} height={35} />
                    </Box>
                  </Box>
                  <Divider light />
                </React.Fragment>
              ))
            : data?.with_entitlement.map((item, key) => {
                return (
                  <React.Fragment key={key}>
                    <Box
                      sx={{
                        width: '100%',
                        p: 2,
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        justifyContent: 'space-between',
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        boxSizing: 'border-box',
                      }}
                    >
                      <Box width={'100%'} display={'flex'} flexDirection={'column'}>
                        <Box mb={1} display={'flex'} flexDirection={'row'} alignItems={'center'}>
                          <Typography noWrap mr={{ xs: 3, lg: 1 }} variant="subtitle1">
                            {item.name}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography
                            width={1}
                            noWrap
                            variant="subtitle2"
                            color="light-gray-text.main"
                          >
                            {item.description}
                          </Typography>
                        </Box>
                      </Box>

                      <Box width={200} display={'flex'} flexDirection={'row'} alignItems={'center'}>
                        <Link
                          component={RouterLink}
                          to={`${item.id}`}
                          state={{
                            backgroundLocation: location,
                            status: item?.status,
                          }}
                        >
                          <Typography
                            color="alternate.main"
                            sx={{
                              width: 100,
                              padding: 1,
                              boxSizing: 'border-box',
                              cursor: 'pointer',
                              fontWeight: 'medium',
                            }}
                            variant="subtitle1"
                          >
                            View Policy
                          </Typography>
                        </Link>
                        {item?.status === 3 ? (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 2,
                            }}
                          >
                            <AdxInfoIcon
                              toolTipTitle={
                                'Please review the configurations in manage leave policy page'
                              }
                            />
                          </Box>
                        ) : (
                          <LeavePolicyModal status={item?.status} item={item} />
                        )}
                      </Box>
                    </Box>
                    <Divider variant="full" />
                  </React.Fragment>
                )
              })}
        </Box>

        <Box sx={{ p: 0.5 }}>
          <AdxButton
            variant={'ghost'}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              width: '100%',
            }}
            onClick={() => {
              navigate('/app/management-centre/company-management/leave/create-policy-entitlement')
            }}
          >
            <Box display={'flex'} flexDirection={'row'} alignItems={'center'} sx={{ gap: 1 }}>
              <AddIcon sx={{ color: 'alternate.main' }}></AddIcon>
              <Typography variant="subtitle1" color={'alternate.main'}>
                Add new policy
              </Typography>
            </Box>
          </AdxButton>
        </Box>
      </Card>

      {/* Without Entilement */}
      <Card
        variant="outlined"
        sx={{
          mb: 1,
          boxSizing: 'border-box',
          borderRadius: '4.5px',
        }}
      >
        <Box display={'flex'} flexDirection={'column'}>
          <Box
            sx={{
              p: 2,
              display: 'flex',
              alignItems: 'center',
              boxSizing: 'border-box',
            }}
          >
            <Typography sx={{ fontWeight: 'bold' }} variant="subtitle1">
              Policy without entitlement
            </Typography>
            <AdxInfoIcon
              sx={{ ml: 2 }}
              toolTipTitle={
                'Policies that require approval from management to be allocated. Employees must apply for these policies request before the allocation is provided. '
              }
            />
          </Box>
          <Divider variant="full" />
          {isLoading
            ? skeletonArray.map((arr, index) => (
                <React.Fragment key={index}>
                  <Box
                    sx={{
                      display: { xs: 'column', sm: 'flex' },
                      justifyContent: 'space-between',
                    }}
                  >
                    <Box p={{ xs: 1, sm: 2 }} width={{ xs: 1, sm: 1 / 3 }}>
                      <Skeleton width={'100%'} height={35} />
                    </Box>
                    <Box p={{ xs: 1, sm: 2 }} width={{ xs: 1, sm: 1 / 4 }}>
                      <Skeleton width={'100%'} height={35} />
                    </Box>
                  </Box>
                  <Divider light />
                </React.Fragment>
              ))
            : data?.without_entitlement?.map((item, key) => {
                return (
                  <React.Fragment key={key}>
                    {isLoading ? (
                      <>
                        <Box
                          sx={{
                            display: { xs: 'column', sm: 'flex' },
                            justifyContent: 'space-between',
                          }}
                        >
                          <Box p={{ xs: 1, sm: 2 }} width={{ xs: 1, sm: 1 / 3 }}>
                            <Skeleton width={'100%'} height={35} />
                          </Box>
                          <Box p={{ xs: 1, sm: 2 }} width={{ xs: 1, sm: 1 / 4 }}>
                            <Skeleton width={'100%'} height={35} />
                          </Box>
                        </Box>
                        <Divider light />
                      </>
                    ) : (
                      <>
                        <Box
                          sx={{
                            width: '100%',
                            p: 2,
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            justifyContent: 'space-between',
                            alignItems: { xs: 'flex-start', sm: 'center' },
                            boxSizing: 'border-box',
                          }}
                        >
                          <Box width={'100%'} display={'flex'} flexDirection={'column'}>
                            <Box
                              mb={1}
                              display={'flex'}
                              flexDirection={'row'}
                              alignItems={'center'}
                            >
                              <Typography noWrap mr={{ xs: 3, sm: 1 }} variant="subtitle1">
                                {item.name}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography
                                width={1}
                                noWrap
                                variant="subtitle2"
                                color="light-gray-text.main"
                              >
                                {item.description}
                              </Typography>
                            </Box>
                          </Box>

                          <Box
                            width={200}
                            display={'flex'}
                            flexDirection={'row'}
                            alignItems={'center'}
                          >
                            <Link
                              component={RouterLink}
                              to={`${item.id}`}
                              state={{
                                backgroundLocation: location,
                              }}
                            >
                              <Typography
                                color="alternate.main"
                                sx={{
                                  width: 100,
                                  padding: 1,
                                  boxSizing: 'border-box',
                                  cursor: 'pointer',
                                  fontWeight: 'medium',
                                }}
                                variant="subtitle1"
                              >
                                View Policy
                              </Typography>
                            </Link>
                            {item?.status === 3 ? (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 2,
                                }}
                              >
                                <AdxInfoIcon
                                  toolTipTitle={
                                    'Please review the configurations in manage leave policy page'
                                  }
                                />
                              </Box>
                            ) : (
                              <LeavePolicyModal status={item?.status} item={item} />
                            )}
                          </Box>
                        </Box>
                        <Divider variant="full" />
                      </>
                    )}
                  </React.Fragment>
                )
              })}
        </Box>

        <Box sx={{ p: 0.5 }}>
          <AdxButton
            variant={'ghost'}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              width: '100%',
            }}
            onClick={() => {
              navigate(
                '/app/management-centre/company-management/leave/create-policy-without-entitlement'
              )
            }}
          >
            <Box display={'flex'} flexDirection={'row'} alignItems={'center'} sx={{ gap: 1 }}>
              <AddIcon sx={{ color: 'alternate.main' }}></AddIcon>
              <Typography variant="subtitle1" color={'alternate.main'}>
                Add new policy
              </Typography>
            </Box>
          </AdxButton>
        </Box>
      </Card>
    </>
  )
}

export default LeavePolicy
